import { Button } from "../../components/Button";
import { Spinner } from "../../components/Spinner";
import { BackButton } from "../../components/BackButton";
import { readinessBand } from "../../readinessBand";

export interface TopicResult {
  topic: string;
  correct: number;
  total: number;
  weak?: boolean;
}

interface SessionSummaryScreenProps {
  subject: string;
  correct: number;
  total: number;
  /** Readiness before this session. `null` on a cold start. */
  readinessBefore: number | null;
  /**
   * Readiness now. `null` when the engine has nothing to go on, or has not
   * finished recomputing.
   *
   * Not a number with 0 standing in for "unknown": that rendered "starts at 0%
   * — early estimate" to a student who had just scored 4 of 10, which is a
   * false statement about them and exactly what the readiness model exists not
   * to do.
   */
  readinessAfter: number | null;
  /** The engine is still recomputing — it recomputes off a queue, not inline. */
  readinessPending?: boolean | undefined;
  confidence: number;
  topics: TopicResult[];
  streakDays: number;
  weakestTopic?: string | undefined;
  onPractiseWeakest?: (() => void) | undefined;
  onHome?: (() => void) | undefined;
}

/**
 * Screen 10 — the hero is the readiness **delta**, not the session score.
 *
 * "7 of 10" is a fact about ten questions; "34% → 37%" is a fact about the exam.
 * The session score stays, small, above it.
 *
 * Cold start deliberately has no delta: a first session shows "starts at 34% —
 * early estimate", because a delta from nothing is an invented number and the
 * whole readiness model rests on not inventing them.
 */
export function SessionSummaryScreen({
  subject,
  correct,
  total,
  readinessBefore,
  readinessAfter,
  readinessPending = false,
  confidence,
  topics,
  streakDays,
  weakestTopic,
  onPractiseWeakest,
  onHome,
}: SessionSummaryScreenProps) {
  const isColdStart = readinessBefore === null;
  const band = readinessBand(confidence, readinessAfter ?? 0);

  return (
    <div className="app-column px-5 pb-6 pt-6">
      {/* The session is over, so back means the hub, not the last question. */}
      <BackButton to="/practice" />

      <p className="m-0 text-[12px] uppercase tracking-[0.12em] text-text-muted">Session done</p>
      <p className="tabular m-0 pt-1 text-[14px] text-text-muted">
        {correct} of {total} correct
      </p>

      <section className="mt-5 flex flex-col items-center gap-2 rounded-[var(--radius-card)] border border-border bg-surface p-5">
        <p className="m-0 text-[12px] text-text-muted">{subject} readiness</p>
        {readinessPending ? (
          /*
           * A spinner, not the number we have so far. Until the recompute
           * lands that number is the old one, and showing "34% → 34%" then
           * changing it a few seconds later reads as a glitch, not a result.
           */
          <div className="flex h-[66px] flex-col items-center justify-center gap-2.5">
            <Spinner size={28} label="Working out your readiness" />
            <p className="m-0 text-[12.5px] text-text-muted">Working out your readiness…</p>
          </div>
        ) : readinessAfter === null ? (
          <>
            <p className="tabular m-0 font-[family-name:var(--font-display)] text-[40px] font-bold leading-none text-text-muted">
              —
            </p>
            <p className="m-0 max-w-[260px] text-center text-[12.5px] leading-[1.45] text-text-muted">
              Not enough yet — a few more questions and this appears.
            </p>
          </>
        ) : isColdStart ? (
          <>
            <p className="tabular m-0 font-[family-name:var(--font-display)] text-[40px] font-bold leading-none">
              {readinessAfter}%
            </p>
            <p className="m-0 text-[12.5px] text-text-muted">
              starts at {readinessAfter}% — early estimate
            </p>
          </>
        ) : (
          <>
            <p className="tabular m-0 flex items-baseline gap-2 font-[family-name:var(--font-display)] text-[40px] font-bold leading-none">
              <span className="text-[24px] text-text-muted line-through decoration-1">
                {readinessBefore}%
              </span>
              <span aria-hidden="true" className="text-[20px] text-text-muted">
                →
              </span>
              <span>{readinessAfter}%</span>
            </p>
            <p className="m-0 text-[12.5px] text-text-muted">{band} — every session moves this</p>
          </>
        )}
      </section>

      <h2 className="m-0 pt-6 text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted">
        By topic
      </h2>
      <ul className="m-0 flex list-none flex-col gap-2 p-0 pt-3">
        {topics.map((topic) => (
          <li
            key={topic.topic}
            className="flex items-center justify-between gap-3 rounded-[var(--radius-control)] border border-border bg-surface px-3.5 py-3"
          >
            <span className="flex items-center gap-2 text-[13.5px]">
              {topic.topic}
              {topic.weak ? (
                <span className="rounded-[var(--radius-chip)] bg-[rgba(228,87,79,0.14)] px-2 py-0.5 text-[10px] font-semibold text-negative">
                  weak
                </span>
              ) : null}
            </span>
            <span className="tabular text-[13px] text-text-muted">
              {topic.correct} of {topic.total}
            </span>
          </li>
        ))}
      </ul>

      <p className="m-0 pt-5 text-[13px] text-text-muted">
        <span className="tabular font-semibold text-text">Day {streakDays}</span> — keep it alive
        tomorrow.
      </p>

      <div className="mt-auto flex flex-col gap-3 pt-8">
        {/* Pre-targeted at the weakest topic: "what next" is a decision the
            summary already has the data to make. */}
        <Button onClick={onPractiseWeakest}>
          {weakestTopic ? `Practice ${weakestTopic}` : "Practice my weakest topic"}
        </Button>
        <Button variant="ghost" onClick={onHome}>
          Back to Home
        </Button>
      </div>
    </div>
  );
}
